import React, { useState } from 'react';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { MasterDataTable } from '../../components/admin/MasterDataTable';
import { MasterDataForm } from '../../components/admin/MasterDataForm';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Building2, Plus } from 'lucide-react';
import { Plant, Building, Floor, Location } from '../../types/masterData';

export const OrganizationConfigPage: React.FC = () => {
  const { plants, buildings, floors, locations, isLoading, createPlant, updatePlant, deletePlant, createBuilding, updateBuilding, deleteBuilding, createFloor, updateFloor, deleteFloor, createLocation, updateLocation, deleteLocation } = useAdminConfig();
  const [subTab, setSubTab] = useState<'plants' | 'buildings' | 'floors' | 'locations'>('plants');
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Record<string, string> | null>(null);
  const [editType, setEditType] = useState<'plant' | 'building' | 'floor' | 'location'>('plant');

  if (isLoading) return <LoadingSpinner size="lg" text="Loading Organization Config..." />;

  const handleAdd = () => { setEditingItem(null); setFormOpen(true); };
  const handleEdit = (item: Plant | Building | Floor | Location) => {
    const typeMap: Record<string, 'plant' | 'building' | 'floor' | 'location'> = {
      plant_id: 'plant', building_id: 'building', floor_id: 'floor', location_id: 'location',
    };
    const typeKey = Object.keys(typeMap).find(k => k in item) || 'plant';
    setEditType(typeMap[typeKey]);
    setEditingItem(item as unknown as Record<string, string>);
    setFormOpen(true);
  };

  const plantFields = [
    { key: 'name', label: 'Plant Name', required: true },
    { key: 'code', label: 'Plant Code', required: true },
  ];
  const buildingFields = [
    { key: 'name', label: 'Building Name', required: true },
    { key: 'plant_id', label: 'Parent Plant', type: 'select' as const, options: plants.map(p => ({ value: p.plant_id, label: p.name })), required: true },
  ];
  const floorFields = [
    { key: 'name', label: 'Floor Name', required: true },
    { key: 'building_id', label: 'Parent Building', type: 'select' as const, options: buildings.map(b => ({ value: b.building_id, label: b.name })), required: true },
  ];
  const locationFields = [
    { key: 'name', label: 'Location Name', required: true },
    { key: 'code', label: 'Location Code', required: true },
    { key: 'floor_id', label: 'Parent Floor', type: 'select' as const, options: floors.map(f => ({ value: f.floor_id, label: f.name })), required: true },
  ];

  const handleSave = async (data: Record<string, string>) => {
    const type = editingItem ? editType : subTab === 'locations' ? 'location' : subTab === 'floors' ? 'floor' : subTab === 'buildings' ? 'building' : 'plant';
    const id = editingItem ? editingItem[`${type === 'plant' ? 'plant' : type === 'building' ? 'building' : type === 'floor' ? 'floor' : 'location'}_id`] : undefined;
    if (type === 'plant') { if (id) await updatePlant(id, data); else await createPlant(data); }
    else if (type === 'building') { if (id) await updateBuilding(id, data); else await createBuilding(data); }
    else if (type === 'floor') { if (id) await updateFloor(id, data); else await createFloor(data); }
    else { if (id) await updateLocation(id, data); else await createLocation(data); }
    setFormOpen(false);
  };

  const handleDelete = async (item: Plant | Building | Floor | Location) => {
    const raw = item as unknown as Record<string, string>;
    const id = raw[`${subTab === 'plants' ? 'plant' : subTab === 'buildings' ? 'building' : subTab === 'floors' ? 'floor' : 'location'}_id`];
    if (subTab === 'plants') await deletePlant(id);
    else if (subTab === 'buildings') await deleteBuilding(id);
    else if (subTab === 'floors') await deleteFloor(id);
    else await deleteLocation(id);
  };

  const tabs = [
    { id: 'plants' as const, label: 'Plants', count: plants.length },
    { id: 'buildings' as const, label: 'Buildings', count: buildings.length },
    { id: 'floors' as const, label: 'Floors', count: floors.length },
    { id: 'locations' as const, label: 'Locations', count: locations.length },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Organization Hierarchy</h3>
          <p className="text-xs text-slate-400">Manage plants, buildings, floors and locations</p>
        </div>
      </div>

      <div className="flex border-b border-slate-800 gap-1">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setSubTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-2 ${
              subTab === tab.id ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'
            }`}>
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <Card>
        {subTab === 'plants' && (
          <MasterDataTable<Plant & Record<string, unknown>>
            data={plants.map(p => ({ ...p }))}
            columns={[
              { key: 'name', label: 'Plant Name', render: item => <span className="font-semibold text-slate-100">{item.name}</span> },
              { key: 'code', label: 'Code', render: item => <Badge label={item.code} variant="info" /> },
              { key: 'plant_id', label: 'ID', render: item => <span className="text-[10px] font-mono text-slate-500">{item.plant_id}</span> },
            ]}
            onAdd={handleAdd} onEdit={handleEdit} onDelete={handleDelete}
            searchKey="name" searchPlaceholder="Search plants..."
          />
        )}
        {subTab === 'buildings' && (
          <MasterDataTable<Building & Record<string, unknown>>
            data={buildings.map(b => ({ ...b }))}
            columns={[
              { key: 'name', label: 'Building Name', render: item => <span className="font-semibold text-slate-100">{item.name}</span> },
              { key: 'plant_id', label: 'Plant', render: item => <span className="text-slate-300">{plants.find(p => p.plant_id === item.plant_id)?.name || item.plant_id}</span> },
              { key: 'building_id', label: 'ID', render: item => <span className="text-[10px] font-mono text-slate-500">{item.building_id}</span> },
            ]}
            onAdd={handleAdd} onEdit={handleEdit} onDelete={handleDelete}
            searchKey="name" searchPlaceholder="Search buildings..."
          />
        )}
        {subTab === 'floors' && (
          <MasterDataTable<Floor & Record<string, unknown>>
            data={floors.map(f => ({ ...f }))}
            columns={[
              { key: 'name', label: 'Floor Name', render: item => <span className="font-semibold text-slate-100">{item.name}</span> },
              { key: 'building_id', label: 'Building', render: item => <span className="text-slate-300">{buildings.find(b => b.building_id === item.building_id)?.name || item.building_id}</span> },
              { key: 'floor_id', label: 'ID', render: item => <span className="text-[10px] font-mono text-slate-500">{item.floor_id}</span> },
            ]}
            onAdd={handleAdd} onEdit={handleEdit} onDelete={handleDelete}
            searchKey="name" searchPlaceholder="Search floors..."
          />
        )}
        {subTab === 'locations' && (
          <MasterDataTable<Location & Record<string, unknown>>
            data={locations.map(l => ({ ...l }))}
            columns={[
              { key: 'name', label: 'Location Name', render: item => <span className="font-semibold text-slate-100">{item.name}</span> },
              { key: 'code', label: 'Code', render: item => <Badge label={item.code} variant="info" /> },
              { key: 'floor_id', label: 'Floor', render: item => <span className="text-slate-300">{floors.find(f => f.floor_id === item.floor_id)?.name || item.floor_id}</span> },
              { key: 'location_id', label: 'ID', render: item => <span className="text-[10px] font-mono text-slate-500">{item.location_id}</span> },
            ]}
            onAdd={handleAdd} onEdit={handleEdit} onDelete={handleDelete}
            searchKey="name" searchPlaceholder="Search locations..."
          />
        )}
      </Card>

      <MasterDataForm
        isOpen={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave}
        title={editingItem ? `Edit ${editType}` : `Add New ${subTab.slice(0, -1)}`}
        fields={subTab === 'plants' ? plantFields : subTab === 'buildings' ? buildingFields : subTab === 'floors' ? floorFields : locationFields}
        initialData={editingItem || undefined}
      />
    </div>
  );
};
